import React, { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { BillingCycle, InitialProps, PricingDetails, ResellingPrograms, SubscriptionTier } from '../../type';
import { useIsDiscountPage } from '../hooks/useIsDiscountPage';
import {
    useBillingCycleSlice,
    useSelectedCountrySlice,
    useSelectedTierSlice,
    useSubscriptionDetailsSlice,
} from './slices';

interface State {
    sellerName: string;
    resellingPrograms: ResellingPrograms;
    isOpenSubscriptionDrawer: boolean;
    subscriptionDetails: Map<BillingCycle, PricingDetails[]>;
    subscriptionDetailsByTier: Map<BillingCycle, PricingDetails>;
    billingCycleState: ReturnType<typeof useBillingCycleSlice>;
    selectedCountryState: ReturnType<typeof useSelectedCountrySlice>;
    selectedTierState: ReturnType<typeof useSelectedTierSlice>;
    pricingDetails?: PricingDetails[];
    annualPlanDiscount: number;
    getSubscriptionDetailsByTier: ReturnType<typeof useSubscriptionDetailsSlice>['getSubscriptionDetailsByTier'];
}

const ResellingProgramsContext = createContext<State | null>(null);

interface ResellingProgramsProviderProps extends PropsWithChildren<InitialProps> {}

export const ResellingProgramsContextProvider = ({
    children,
    sellerName,
    resellingPrograms,
    isOpenSubscriptionDrawer,
}: ResellingProgramsProviderProps) => {
    const isDiscountPage = useIsDiscountPage();

    const billingCycleState = useBillingCycleSlice(isDiscountPage ? { defaultCycle: BillingCycle.YEARLY } : {});
    const selectedCountryState = useSelectedCountrySlice({ sellerName, countries: resellingPrograms.countries });
    const selectedTierState = useSelectedTierSlice({ sellerName });

    const { subscriptionDetails, getPricingDetailsByBillingCycle, getSubscriptionDetailsByTier } =
        useSubscriptionDetailsSlice({
            currentCountry: selectedCountryState.selectedCountry,
            products: resellingPrograms.products,
        });

    const pricingDetails = useMemo(
        () => getPricingDetailsByBillingCycle(billingCycleState.billingCycle),
        [billingCycleState.billingCycle, getPricingDetailsByBillingCycle]
    );
    const subscriptionDetailsByTier = useMemo(
        () => getSubscriptionDetailsByTier(selectedTierState.selectedTier),
        [selectedTierState.selectedTier, getSubscriptionDetailsByTier]
    );

    const annualPlanDiscount = useMemo(() => {
        const tierDetails = getSubscriptionDetailsByTier(SubscriptionTier.Standard);

        const monthlyPrice = tierDetails.get(BillingCycle.MONTHLY)?.price?.amountMonthly ?? 0;
        const yearlyPrice = tierDetails.get(BillingCycle.YEARLY)?.price?.amountMonthly ?? 0;

        if (!monthlyPrice || !yearlyPrice) {
            return 0;
        }

        const discount = 100 - (yearlyPrice / monthlyPrice) * 100;

        return Math.floor(discount);
    }, [getSubscriptionDetailsByTier]);

    return (
        <ResellingProgramsContext.Provider
            value={{
                sellerName,
                resellingPrograms,
                isOpenSubscriptionDrawer,
                pricingDetails,
                annualPlanDiscount,
                subscriptionDetails,
                subscriptionDetailsByTier,
                billingCycleState,
                selectedCountryState,
                selectedTierState,
                getSubscriptionDetailsByTier,
            }}
        >
            {children}
        </ResellingProgramsContext.Provider>
    );
};

export const useResellingProgramsContext = () => {
    const context = useContext(ResellingProgramsContext);

    if (!context) {
        throw new Error('useResellingProgramsContext must be used within ResellingProgramsContextProvider');
    }

    return context;
};
